import React, { useState, useEffect, useRef } from 'react';
import { collection, addDoc, getDocs, doc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import PublicNavigation from '../../components/PublicNavigation';
import './ManualProjectEntry.css';

const TN_DISTRICTS = ['Ariyalur','Chennai','Coimbatore','Cuddalore','Dharmapuri','Dindigul','Erode','Kallakurichi','Kancheepuram','Karur','Krishnagiri','Madurai','Mayiladuthurai','Nagapattinam','Namakkal','Perambalur','Pudukkottai','Ramanathapuram','Ranipet','Salem','Sivagangai','Tenkasi','Thanjavur','Theni','Thoothukudi','Tiruchirappalli','Tirunelveli','Tirupattur','Tiruppur','Tiruvallur','Tiruvannamalai','Tiruvarur','Vellore','Viluppuram','Virudhunagar'];

const CATEGORIES = {
  chapter: ['Education Enhancement and Empowering Youth','Special School for Intellectually Challenged Students','Scholarship Projects','Women Empowerment','Taymai Grant'],
  individual: ['Education Enhancement and Empowering Youth','Scholarship Project','Women Empowerment','Taymai Grant'],
  common: ['Awareness Campaign','Gaja Cyclone Rehab','Covid 19','Michaung Cyclone','Rennovation Projects']
};

const BENEFICIARY_CATEGORIES = ['Baseline Test','Soft Skills','Career Aspiration','Library','Uniform','Female Health and Hygiene','Nutritional Supplement','Teacher Training'];

const FUND_SOURCES = ['Local', 'FCRA', 'TNF USA', 'Government Grant', 'Corporate CSR', 'Individual Donor', 'Other'];

function Autocomplete({ label, value, onChange, options, placeholder, required }) {
  const [search, setSearch] = useState(value || '');
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const inputRef = useRef(null);
  
  const filtered = options.filter(opt => 
    opt.toLowerCase().includes(search.toLowerCase())
  ).slice(0, 10);

  useEffect(() => {
    setSearch(value || '');
  }, [value]);

  const handleSelect = (option) => {
    setSearch(option);
    onChange(option);
    setShowDropdown(false);
    setHighlightIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex(prev => Math.min(prev + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex(prev => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter' && highlightIndex >= 0) {
      e.preventDefault();
      handleSelect(filtered[highlightIndex]);
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  return (
    <div className="autocomplete">
      <label>{label} {required && '*'}</label>
      <input
        ref={inputRef}
        type="text"
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          onChange(e.target.value);
          setShowDropdown(true);
          setHighlightIndex(-1);
        }}
        onFocus={() => setShowDropdown(true)}
        onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        required={required}
      />
      {showDropdown && filtered.length > 0 && (
        <div className="autocomplete-dropdown">
          {filtered.map((option, idx) => (
            <div
              key={option}
              className={`autocomplete-item ${idx === highlightIndex ? 'highlighted' : ''}`}
              onMouseDown={() => handleSelect(option)}
            >
              {option}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ManualProjectEntry() {
  const { currentUser } = useAuth();
  const [quickMode, setQuickMode] = useState(false);
  const [viewMode, setViewMode] = useState('entry');
  const [allProjects, setAllProjects] = useState([]);
  const [editingId, setEditingId] = useState(null);
  
  const [formData, setFormData] = useState({
    donorType: 'chapter',
    category: '',
    chapter: '',
    district: '',
    donorName: '',
    projectDescription: '',
    startYear: new Date().getFullYear(),
    status: 'Active',
    projectCoordinator: '',
    reportMonth: '',
    reportYear: new Date().getFullYear(),
    beneficiaryName: '',
    beneficiaryCategories: {},
    beneficiaryCategoryDates: {},
    totalBeneficiaries: 0,
    fundSources: [{
      source: 'Local',
      openingBalance: 0,
      received: 0,
      disbursed: 0,
      closingBalance: 0
    }],
    totalFunds: {
      openingBalance: 0,
      received: 0,
      disbursed: 0,
      closingBalance: 0
    },
    photoLinks: [{title: '', link: ''}],
    projectUpdates: [{date: new Date().toISOString().split('T')[0], update: ''}]
  });

  const [chapters, setChapters] = useState([]);
  const [coordinators, setCoordinators] = useState([]);
  const [donors, setDonors] = useState([]);
  const [customCategories, setCustomCategories] = useState([]);
  const [customBeneficiaryCategories, setCustomBeneficiaryCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [listFilters, setListFilters] = useState({
    donorType: '',
    chapter: '',
    district: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    calculateTotalFunds();
  }, [JSON.stringify(formData.fundSources)]);

  useEffect(() => {
    const total = Object.values(formData.beneficiaryCategories).reduce((sum, val) => sum + (parseInt(val) || 0), 0);
    setFormData(prev => ({ ...prev, totalBeneficiaries: total }));
  }, [formData.beneficiaryCategories]);

  useEffect(() => {
    if (formData.district && coordinators.length > 0) {
      const coord = coordinators.find(c => c.districts && c.districts.includes(formData.district));
      if (coord) {
        setFormData(prev => ({ ...prev, projectCoordinator: coord.name }));
      }
    }
  }, [formData.district, coordinators]);

  const calculateTotalFunds = () => {
    const updatedSources = formData.fundSources.map(source => {
      const opening = parseFloat(source.openingBalance) || 0;
      const received = parseFloat(source.received) || 0;
      const disbursed = parseFloat(source.disbursed) || 0;
      return {
        ...source,
        closingBalance: opening + received - disbursed
      };
    });

    const totalOpening = updatedSources.reduce((sum, s) => sum + (parseFloat(s.openingBalance) || 0), 0);
    const totalReceived = updatedSources.reduce((sum, s) => sum + (parseFloat(s.received) || 0), 0);
    const totalDisbursed = updatedSources.reduce((sum, s) => sum + (parseFloat(s.disbursed) || 0), 0);
    const totalClosing = totalOpening + totalReceived - totalDisbursed;

    setFormData(prev => ({
      ...prev,
      fundSources: updatedSources,
      totalFunds: {
        openingBalance: totalOpening,
        received: totalReceived,
        disbursed: totalDisbursed,
        closingBalance: totalClosing
      }
    }));
  };

  async function loadData() {
    try {
      const [chapSnap, coordSnap, donorSnap, catSnap, beneCatSnap, projSnap] = await Promise.all([
        getDocs(collection(db, 'chapters')),
        getDocs(collection(db, 'coordinators')),
        getDocs(collection(db, 'donors')),
        getDocs(collection(db, 'customCategories')),
        getDocs(collection(db, 'beneficiaryCategories')),
        getDocs(collection(db, 'manualProjects'))
      ]);
      
      setChapters(chapSnap.docs.map(d => ({id: d.id, ...d.data()})));
      setCoordinators(coordSnap.docs.map(d => ({id: d.id, ...d.data()})));
      setDonors(donorSnap.docs.map(d => ({id: d.id, ...d.data()})));
      setCustomCategories(catSnap.docs.map(d => d.data().name));
      setCustomBeneficiaryCategories(beneCatSnap.docs.map(d => d.data().name));
      setAllProjects(projSnap.docs.map(d => ({id: d.id, ...d.data()})));
    } catch (error) {
      console.error('Load error:', error);
      alert('Error loading data: ' + error.message);
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleBeneficiaryCategoryChange = (category, value) => {
    setFormData(prev => ({
      ...prev,
      beneficiaryCategories: {
        ...prev.beneficiaryCategories,
        [category]: value
      }
    }));
  };

  const handleBeneficiaryCategoryDateChange = (category, date) => {
    setFormData(prev => ({
      ...prev,
      beneficiaryCategoryDates: {
        ...prev.beneficiaryCategoryDates,
        [category]: date
      }
    }));
  };

  const handleFundSourceChange = (index, field, value) => {
    const newSources = [...formData.fundSources];
    newSources[index][field] = value;
    setFormData(prev => ({ ...prev, fundSources: newSources }));
  };

  const addFundSource = () => {
    setFormData(prev => ({
      ...prev,
      fundSources: [...prev.fundSources, {
        source: 'Local',
        openingBalance: 0,
        received: 0,
        disbursed: 0,
        closingBalance: 0
      }]
    }));
  };

  const removeFundSource = (index) => {
    if (formData.fundSources.length <= 1) {
      alert('At least one fund source is required');
      return;
    }
    setFormData(prev => ({
      ...prev,
      fundSources: prev.fundSources.filter((_, i) => i !== index)
    }));
  };

  const handlePhotoLinkChange = (index, field, value) => {
    const newPhotoLinks = [...formData.photoLinks];
    newPhotoLinks[index][field] = value;
    setFormData(prev => ({ ...prev, photoLinks: newPhotoLinks }));
  };

  const addPhotoLink = () => {
    setFormData(prev => ({
      ...prev,
      photoLinks: [...prev.photoLinks, {title: '', link: ''}]
    }));
  };

  const removePhotoLink = (index) => {
    setFormData(prev => ({
      ...prev,
      photoLinks: prev.photoLinks.filter((_, i) => i !== index)
    }));
  };

  const handleProjectUpdateChange = (index, field, value) => {
    const newUpdates = [...formData.projectUpdates];
    newUpdates[index][field] = value;
    setFormData(prev => ({ ...prev, projectUpdates: newUpdates }));
  };

  const addProjectUpdate = () => {
    setFormData(prev => ({
      ...prev,
      projectUpdates: [...prev.projectUpdates, {date: new Date().toISOString().split('T')[0], update: ''}]
    }));
  };

  const removeProjectUpdate = (index) => {
    setFormData(prev => ({
      ...prev,
      projectUpdates: prev.projectUpdates.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const dataToSave = {
        ...formData,
        totalBeneficiaries: parseInt(formData.totalBeneficiaries) || 0,
        lastModified: serverTimestamp()
      };

      if (editingId) {
        await updateDoc(doc(db, 'manualProjects', editingId), dataToSave);
        alert('✅ Project updated!');
      } else {
        dataToSave.createdAt = serverTimestamp();
        dataToSave.createdBy = currentUser.email;
        await addDoc(collection(db, 'manualProjects'), dataToSave);
        alert('✅ Project saved!');
      }
      
      if (quickMode && !editingId) {
        resetForm();
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.error('Save error:', error);
      alert('Save failed: ' + error.message);
    }
    setSaving(false);
  };

  const handleEdit = (project) => {
    // Convert old fund format to new format for backward compatibility
    let fundSources;
    
    if (project.fundSources && Array.isArray(project.fundSources) && project.fundSources.length > 0) {
      // Already has new format
      fundSources = project.fundSources;
    } else {
      // Old format - convert to new format
      fundSources = [{
        source: 'Local',
        openingBalance: project.fundOpeningBalance || 0,
        received: project.fundReceived || 0,
        disbursed: project.fundDisbursed || 0,
        closingBalance: project.fundClosingBalance || 0
      }];
    }

    setFormData({
      donorType: project.donorType || 'chapter',
      category: project.category || '',
      chapter: project.chapter || '',
      district: project.district || '',
      donorName: project.donorName || '',
      projectDescription: project.projectDescription || '',
      startYear: project.startYear || new Date().getFullYear(),
      status: project.status || 'Active',
      projectCoordinator: project.projectCoordinator || '',
      reportMonth: project.reportMonth || '',
      reportYear: project.reportYear || new Date().getFullYear(),
      beneficiaryName: project.beneficiaryName || '',
      beneficiaryCategories: project.beneficiaryCategories || {},
      beneficiaryCategoryDates: project.beneficiaryCategoryDates || {},
      totalBeneficiaries: project.totalBeneficiaries || 0,
      fundSources: fundSources,
      totalFunds: {
        openingBalance: 0,
        received: 0,
        disbursed: 0,
        closingBalance: 0
      },
      photoLinks: project.photoLinks || [{title: '', link: ''}],
      projectUpdates: project.projectUpdates || [{date: new Date().toISOString().split('T')[0], update: ''}]
    });
    
    setEditingId(project.id);
    setViewMode('entry');
    window.scrollTo(0, 0);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this project? This cannot be undone.')) return;
    try {
      await deleteDoc(doc(db, 'manualProjects', id));
      alert('✅ Deleted!');
      loadData();
    } catch (error) {
      alert('Delete failed: ' + error.message);
    }
  };

  const resetForm = () => {
    setFormData({
      donorType: formData.donorType,
      category: '',
      chapter: formData.chapter,
      district: formData.district,
      donorName: formData.donorName,
      projectDescription: '',
      startYear: new Date().getFullYear(),
      status: 'Active',
      projectCoordinator: formData.projectCoordinator,
      reportMonth: formData.reportMonth,
      reportYear: formData.reportYear,
      beneficiaryName: '',
      beneficiaryCategories: {},
      beneficiaryCategoryDates: {},
      totalBeneficiaries: 0,
      fundSources: [{
        source: 'Local',
        openingBalance: 0,
        received: 0,
        disbursed: 0,
        closingBalance: 0
      }],
      totalFunds: {
        openingBalance: 0,
        received: 0,
        disbursed: 0,
        closingBalance: 0
      },
      photoLinks: [{title: '', link: ''}],
      projectUpdates: [{date: new Date().toISOString().split('T')[0], update: ''}]
    });
    setEditingId(null);
  };

  const addCategory = async () => {
    const name = prompt('Enter category name:');
    if (!name) return;
    try {
      await addDoc(collection(db, 'customCategories'), { name, type: formData.donorType });
      setCustomCategories(prev => [...prev, name]);
      setFormData(prev => ({ ...prev, category: name }));
      alert('✅ Category added!');
    } catch (error) {
      alert('Failed: ' + error.message);
    }
  };

  const addBeneficiaryCategory = async () => {
    const name = prompt('Enter beneficiary category:');
    if (!name) return;
    try {
      await addDoc(collection(db, 'beneficiaryCategories'), { name });
      setCustomBeneficiaryCategories(prev => [...prev, name]);
      alert('✅ Category added!');
    } catch (error) {
      alert('Failed: ' + error.message);
    }
  };

  const availableCategories = [...CATEGORIES[formData.donorType], ...customCategories];
  const allBeneficiaryCategories = [...BENEFICIARY_CATEGORIES, ...customBeneficiaryCategories];

  const chapterOptions = chapters.map(c => c.name);
  const donorOptions = formData.donorType === 'chapter' ? chapterOptions : donors.filter(d => d.type === formData.donorType).map(d => d.name);

  const uniqueChapters = [...new Set(allProjects.map(p => p.chapter).filter(Boolean))].sort();
  const uniqueDistricts = [...new Set(allProjects.map(p => p.district).filter(Boolean))].sort();

  const getFilteredList = () => {
    return allProjects.filter(p => {
      if (listFilters.donorType && p.donorType !== listFilters.donorType) return false;
      if (listFilters.chapter && p.chapter !== listFilters.chapter) return false;
      if (listFilters.district && p.district !== listFilters.district) return false;
      return true;
    });
  };

  return (
    <div className="manual-entry-page">
      <PublicNavigation />
      <div className="manual-entry-container">
        <div className="header-bar">
          <h1>📝 Manual Project Entry</h1>
          <div className="header-actions">
            <button 
              className={`mode-btn ${viewMode === 'entry' ? 'active' : ''}`}
              onClick={() => setViewMode('entry')}
            >
              + Add/Edit
            </button>
            <button 
              className={`mode-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
            >
              📋 View All ({allProjects.length})
            </button>
          </div>
        </div>

        {viewMode === 'list' ? (
          <div className="projects-list">
            <div className="list-filters">
              <div className="filter-row">
                <div className="filter-field">
                  <label>Donor Type:</label>
                  <select value={listFilters.donorType} onChange={(e) => setListFilters({...listFilters, donorType: e.target.value})}>
                    <option value="">All Types</option>
                    <option value="chapter">Chapter</option>
                    <option value="individual">Individual</option>
                    <option value="common">Common</option>
                  </select>
                </div>
                <div className="filter-field">
                  <label>Chapter:</label>
                  <select value={listFilters.chapter} onChange={(e) => setListFilters({...listFilters, chapter: e.target.value})}>
                    <option value="">All Chapters</option>
                    {uniqueChapters.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="filter-field">
                  <label>District:</label>
                  <select value={listFilters.district} onChange={(e) => setListFilters({...listFilters, district: e.target.value})}>
                    <option value="">All Districts</option>
                    {uniqueDistricts.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <button onClick={() => setListFilters({donorType: '', chapter: '', district: ''})} className="clear-filters-btn">
                  Clear Filters
                </button>
              </div>
            </div>
            <table className="list-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Category</th>
                  <th>Chapter</th>
                  <th>District</th>
                  <th>Donor</th>
                  <th>Beneficiaries</th>
                  <th>Total Funds</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {getFilteredList().map(p => {
                  const totalFunds = p.totalFunds?.received || p.fundReceived || 0;
                  return (
                    <tr key={p.id}>
                      <td><span className={`type-badge ${p.donorType}`}>{p.donorType?.toUpperCase()}</span></td>
                      <td>{p.category}</td>
                      <td>{p.chapter || '-'}</td>
                      <td>{p.district}</td>
                      <td>{p.donorName}</td>
                      <td>{p.totalBeneficiaries || 0}</td>
                      <td>₹{totalFunds.toLocaleString('en-IN')}</td>
                      <td><span className={`status-badge ${p.status?.toLowerCase()}`}>{p.status}</span></td>
                      <td>
                        <button onClick={() => handleEdit(p)} className="edit-btn">✏️ Edit</button>
                        <button onClick={() => handleDelete(p.id)} className="delete-btn">🗑️ Delete</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {getFilteredList().length === 0 && (
              <div className="no-results">No projects match your filters</div>
            )}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="simple-form">
            {editingId && (
              <div className="editing-banner">
                Editing Project • <button type="button" onClick={resetForm} className="cancel-edit">Cancel & Add New</button>
              </div>
            )}

            <div className="tabs">
              {['chapter', 'individual', 'common'].map(type => (
                <button key={type} type="button" className={formData.donorType === type ? 'active' : ''} onClick={() => setFormData(prev => ({ ...prev, donorType: type, category: '' }))}>{type.toUpperCase()}</button>
              ))}
            </div>

            <div className="form-row">
              <Autocomplete
                label="Category"
                value={formData.category}
                onChange={(val) => setFormData(prev => ({ ...prev, category: val }))}
                options={availableCategories}
                placeholder="Start typing..."
                required
              />

              {formData.donorType === 'chapter' && (
                <Autocomplete
                  label="Chapter"
                  value={formData.chapter}
                  onChange={(val) => setFormData(prev => ({ ...prev, chapter: val }))}
                  options={chapterOptions}
                  placeholder="Start typing..."
                />
              )}

              <Autocomplete
                label="District"
                value={formData.district}
                onChange={(val) => setFormData(prev => ({ ...prev, district: val }))}
                options={TN_DISTRICTS}
                placeholder="Start typing..."
                required
              />
            </div>

            <div className="form-row">
              <Autocomplete
                label="Donor"
                value={formData.donorName}
                onChange={(val) => setFormData(prev => ({ ...prev, donorName: val }))}
                options={donorOptions}
                placeholder="Start typing..."
                required
              />

              <div className="field">
                <label>Coordinator (Auto)</label>
                <input type="text" value={formData.projectCoordinator} readOnly className="readonly" />
              </div>

              <div className="field">
                <label>Start Year *</label>
                <input type="number" name="startYear" value={formData.startYear} onChange={handleChange} required min="2000" max="2050" />
              </div>
            </div>

            <div className="form-row">
              <div className="field">
                <label>Status</label>
                <select name="status" value={formData.status} onChange={handleChange}>
                  <option>Planning</option><option>Active</option><option>Completed</option><option>On Hold</option>
                </select>
              </div>

              <div className="field">
                <label>Report Month *</label>
                <select name="reportMonth" value={formData.reportMonth} onChange={handleChange} required>
                  <option value="">Select</option>
                  {['January','February','March','April','May','June','July','August','September','October','November','December'].map(m => <option key={m}>{m}</option>)}
                </select>
              </div>

              <div className="field">
                <label>Report Year *</label>
                <input type="number" name="reportYear" value={formData.reportYear} onChange={handleChange} required min="2000" max="2050" />
              </div>
            </div>

            <div className="field">
              <label>Project Description *</label>
              <textarea name="projectDescription" value={formData.projectDescription} onChange={handleChange} required rows="3" placeholder="Enter project details..." />
            </div>

            {!quickMode && (
              <>
                <h3>👥 Beneficiaries <button type="button" onClick={addBeneficiaryCategory} className="add-btn">+ Add Category</button></h3>
                <div className="bene-grid-with-dates">
                  {allBeneficiaryCategories.map(cat => (
                    <div key={cat} className="bene-field-with-date">
                      <label>{cat}</label>
                      <input
                        type="number"
                        min="0"
                        placeholder="Count"
                        value={formData.beneficiaryCategories[cat] || ''}
                        onChange={(e) => handleBeneficiaryCategoryChange(cat, e.target.value)}
                      />
                      <input
                        type="date"
                        placeholder="Date"
                        value={formData.beneficiaryCategoryDates[cat] || ''}
                        onChange={(e) => handleBeneficiaryCategoryDateChange(cat, e.target.value)}
                      />
                    </div>
                  ))}
                </div>
                <div className="total-bene">Total: <strong>{formData.totalBeneficiaries}</strong></div>

                <div className="field">
                  <label>Beneficiary Names / School Names (Optional)</label>
                  <textarea name="beneficiaryName" value={formData.beneficiaryName} onChange={handleChange} rows="2" placeholder="Enter names, one per line" />
                </div>
              </>
            )}

            <h3>💰 Funds (INR) <button type="button" onClick={addFundSource} className="add-btn">+ Add Fund Source</button></h3>
            
            {formData.fundSources.map((fund, index) => (
              <div key={index} className="fund-source-card">
                <div className="fund-header">
                  <select 
                    value={fund.source} 
                    onChange={(e) => handleFundSourceChange(index, 'source', e.target.value)}
                    className="fund-source-select"
                  >
                    {FUND_SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {formData.fundSources.length > 1 && (
                    <button type="button" onClick={() => removeFundSource(index)} className="remove-fund-btn">✕ Remove</button>
                  )}
                </div>
                
                <div className="fund-row">
                  <div className="field">
                    <label>Opening (₹)</label>
                    <input 
                      type="number" 
                      value={fund.openingBalance} 
                      onChange={(e) => handleFundSourceChange(index, 'openingBalance', e.target.value)}
                      step="0.01"
                    />
                  </div>
                  <div className="field">
                    <label>Received (₹)</label>
                    <input 
                      type="number" 
                      value={fund.received} 
                      onChange={(e) => handleFundSourceChange(index, 'received', e.target.value)}
                      step="0.01"
                    />
                  </div>
                  <div className="field">
                    <label>Disbursed (₹)</label>
                    <input 
                      type="number" 
                      value={fund.disbursed} 
                      onChange={(e) => handleFundSourceChange(index, 'disbursed', e.target.value)}
                      step="0.01"
                    />
                  </div>
                  <div className="field">
                    <label>Closing (₹)</label>
                    <input 
                      type="number" 
                      value={fund.closingBalance} 
                      readOnly 
                      className="readonly"
                    />
                  </div>
                </div>
              </div>
            ))}

            <div className="total-funds-card">
              <h4>Total Funds (All Sources)</h4>
              <div className="fund-row">
                <div className="total-item">
                  <span>Opening:</span>
                  <strong>₹{formData.totalFunds.openingBalance.toLocaleString('en-IN')}</strong>
                </div>
                <div className="total-item">
                  <span>Received:</span>
                  <strong>₹{formData.totalFunds.received.toLocaleString('en-IN')}</strong>
                </div>
                <div className="total-item">
                  <span>Disbursed:</span>
                  <strong>₹{formData.totalFunds.disbursed.toLocaleString('en-IN')}</strong>
                </div>
                <div className="total-item highlight">
                  <span>Closing Balance:</span>
                  <strong>₹{formData.totalFunds.closingBalance.toLocaleString('en-IN')}</strong>
                </div>
              </div>
            </div>

            {!quickMode && (
              <>
                <h3>📷 Photo Links <button type="button" onClick={addPhotoLink} className="add-btn">+ Add Photo</button></h3>
                {formData.photoLinks.map((photo, index) => (
                  <div key={index} className="dynamic-row">
                    <input type="text" placeholder="Title" value={photo.title} onChange={(e) => handlePhotoLinkChange(index, 'title', e.target.value)} />
                    <input type="url" placeholder="Google Drive Link" value={photo.link} onChange={(e) => handlePhotoLinkChange(index, 'link', e.target.value)} />
                    {formData.photoLinks.length > 1 && (
                      <button type="button" onClick={() => removePhotoLink(index)} className="remove-btn">✕</button>
                    )}
                  </div>
                ))}

                <h3>📝 Project Updates <button type="button" onClick={addProjectUpdate} className="add-btn">+ Add Update</button></h3>
                {formData.projectUpdates.map((update, index) => (
                  <div key={index} className="dynamic-row">
                    <input type="date" value={update.date} onChange={(e) => handleProjectUpdateChange(index, 'date', e.target.value)} />
                    <textarea placeholder="Update description" value={update.update} onChange={(e) => handleProjectUpdateChange(index, 'update', e.target.value)} rows="2" />
                    {formData.projectUpdates.length > 1 && (
                      <button type="button" onClick={() => removeProjectUpdate(index)} className="remove-btn">✕</button>
                    )}
                  </div>
                ))}
              </>
            )}

            <div className="button-row">
              <button type="submit" className="submit-btn" disabled={saving}>
                {saving ? 'Saving...' : editingId ? '💾 Update Project' : (quickMode ? '⚡ Save & Add Another' : '💾 Save Project')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default ManualProjectEntry;
